import { createElement, Fragment, RefObject, useState, ComponentProps, createRef } from 'react';
import { VariableSizeGrid } from './VariableSizeGrid';
import { useIsRTL } from '../../../locale';
import { TableColumnProps } from '../shared';
import { CellContent, hasSelectionColumn, HeaderCellContent, TableItemData } from './internal';
import { SelectionCellContent, SelectAllCellContent } from './SelectionCellContent';

import '../Table.css';

export const TableCell: ComponentProps<typeof VariableSizeGrid>['children'] = (props: any) => {
  // NOTE: props.data is typed as `any`
  const isRTL = useIsRTL();
  const itemData = props.data as TableItemData;
  const [isDragging, setIsDragging] = useState(false);
  const isSelectionControl = hasSelectionColumn(
    itemData.selectionMode,
    itemData.columns.length,
    itemData.rowCount,
    itemData.hideSelectionControlColumn
  );
  const columnIndex = isSelectionControl ? props.columnIndex - 1 : props.columnIndex;
  const {
    onSort,
    renderCell,
    selectionMode,
    hideSelectAll,
    isRowDisabled,
    isRowReadOnly,
    getRowIdentifier: getRowKey,
  } = itemData;
  const column: TableColumnProps = itemData.columns[props.columnIndex];
  if (!column) {
    return <Fragment />;
  }
  const rowKey = props.rowIndex !== -1 ? getRowKey(props.rowIndex) : undefined;
  const readOnly = itemData.readOnly || (rowKey ? !!isRowReadOnly?.(rowKey) : false);
  const disabled =
    itemData.disabled || (props.rowIndex > -1 && !!isRowDisabled?.(getRowKey(props.rowIndex)));
  if (
    props.columnIndex === 0 &&
    props.isHeader &&
    selectionMode === 'multiple' &&
    hideSelectAll !== true &&
    itemData.rowCount > 0 &&
    itemData.hideSelectionControlColumn !== true
  ) {
    return <SelectAllCellContent rowIndex={props.rowIndex} itemData={itemData} />;
  } else if (props.isHeader) {
    return (
      <HeaderCellContent
        itemData={itemData}
        style={props.style}
        columnIndex={columnIndex}
        column={column}
        sort={(e: React.MouseEvent<HTMLElement>) => {
          // do not sort if we're dragging to resize the column
          if (!isDragging) onSort(e, column.key, true);
        }}
        onDragStart={() => {
          setIsDragging(true);
        }}
        onDragEnd={() => {
          setIsDragging(false);
          itemData.onColumnResize?.(
            column.key,
            itemData.preference?.resizedColumns?.has(column.key)
              ? itemData.preference?.resizedColumns?.get(column.key) || 0
              : column.width || 0
          );
        }}
        onDrag={(deltaPX) => {
          const index = hasSelectionColumn(
            itemData.selectionMode,
            itemData.columns.length,
            itemData.rowCount,
            itemData.hideSelectionControlColumn
          )
            ? columnIndex + 1
            : columnIndex;
          isDragging && itemData.resizeColumn((isRTL ? -1 : 1) * deltaPX, index);
        }}
      />
    );
  } else if (column && column.cellContent) {
    const cellContent = column.cellContent;
    const ref: RefObject<any> = createRef();

    itemData.updateControlRefs(column.key, getRowKey(props.rowIndex), ref);
    let focusCellContent = itemData.preference?.scrollPosition?.editOnFocus;
    if (focusCellContent) {
      const cellToFocus = itemData.preference?.scrollPosition?.cell;
      const rowIndex = cellToFocus?.rowIndex;
      const colIndex = cellToFocus?.colIndex;
      const colKey = colIndex !== undefined ? itemData.columns[colIndex].key : '';
      focusCellContent = rowIndex === props.rowIndex && colKey === column.key;
    }
    return (
      <CellContent
        rowIndex={props.rowIndex}
        columnKey={column.key}
        itemData={itemData}
        className={column.cellContainerClassName}
        style={column.cellContainerStyle}
        focus={focusCellContent}
        contentRef={ref}
      >
        {cellContent(columnIndex, props.rowIndex, column.key, ref, disabled, readOnly)}
      </CellContent>
    );
  } else {
    if (isSelectionControl && props.columnIndex === 0) {
      return <SelectionCellContent rowIndex={props.rowIndex} itemData={itemData} />;
    } else {
      const ref: RefObject<any> = createRef();
      itemData.updateControlRefs(column.key, getRowKey(props.rowIndex), ref);
      return (
        <CellContent
          rowIndex={props.rowIndex}
          columnKey={column.key}
          itemData={itemData}
          className={column.cellContainerClassName}
          style={column.cellContainerStyle}
          contentRef={ref}
        >
          {renderCell({
            rowIndex: props.rowIndex,
            columnIndex,
            isScrolling: props.isScrolling,
            columnKey: column.key,
            disabled,
            readOnly,
            ref,
            itemData,
          })}
        </CellContent>
      );
    }
  }
};
